import { NodeId } from 'web';
import {
  rebaseChildPosition,
  constrainBoundsToChildren,
  calculateMultiNodeBoundsWithPadding,
  convertChildPositionToAbsolute,
} from '@/components/Nodes/utils/nodeUtils';
import { log } from '@/logger/logger';
import { CustomGroupData, Node } from '@/types/node';
import { NODE_GROUPING_EVENT_NAMES } from '@/consts/analytics.consts';
import { TrackTypeEnum } from '@/hooks/useAnalytics';
import { track } from '@/utils/analytics';
import { FlowGraph } from '../FlowGraph';
import { UndoRedoEntry } from '../undoRedo';
import { FlowView } from './FlowView';
import { DEFAULT_GROUP_PADDING, GroupBounds, LABEL_SIZE } from './NodeGrouping/groups.types';

const logger = log.getLogger('CustomGroupView');
export class CustomGroupView {
  private flowView: FlowView;
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {
    this.flowView = new FlowView(graph);
  }

  hasAnyChildren(): boolean {
    const childNodes = this.graph.getChildNodesOfGroup(this.nodeId);
    return childNodes.length > 0;
  }

  updateGroupName(name: string) {
    const trimmed = name.trim();
    const prevName = this.getGroupName() ?? '';
    if (trimmed === '' || trimmed === prevName.trim()) {
      return;
    }
    this.graph.edit(() => {
      return this.graph.store.updateNodeData<CustomGroupData>(this.nodeId, (data) => ({
        ...data,
        name: trimmed,
      }));
    });
    track(
      NODE_GROUPING_EVENT_NAMES.RENAMED,
      { groupId: this.nodeId, prevName, newName: trimmed },
      TrackTypeEnum.Product,
    );
  }

  ungroup(): void {
    const children = this.graph.getChildNodesOfGroup(this.nodeId);

    this.graph.edit(() => {
      const undoEntry = UndoRedoEntry.empty();

      // Remove children from group (converts positions to absolute)
      if (children.length > 0) {
        undoEntry.add(this.removeNodesFromGroup(children));
      }

      // Delete the group node
      undoEntry.add(this.graph.removeNodes([this.nodeId]));

      return undoEntry;
    });

    track(
      NODE_GROUPING_EVENT_NAMES.UNGROUPED,
      {
        groupId: this.nodeId,
        nodeIds: children.map((child) => child.id),
        nodeTypes: children.map((child) => child.type),
        numberOfNodes: children.length,
      },
      TrackTypeEnum.Product,
    );
  }

  updateGroupColor(color: string): void {
    this.graph.edit(() => {
      return this.graph.updateNodeData(this.nodeId, { color });
    });
    track(NODE_GROUPING_EVENT_NAMES.COLOR_CHANGED, { groupId: this.nodeId, color }, TrackTypeEnum.Product);
  }

  updateGroupLabelSize(labelFontSize: number): void {
    this.graph.edit(() => {
      return this.graph.store.updateNodeData<CustomGroupData>(this.nodeId, () => ({ labelFontSize }));
    });
    track(NODE_GROUPING_EVENT_NAMES.LABEL_SIZE_CHANGED, { groupId: this.nodeId, labelFontSize }, TrackTypeEnum.Product);
  }

  getGroupColor(): string | undefined {
    return this.graph.store.getNodeDataNullable<CustomGroupData>(this.nodeId)?.color;
  }

  getGroupLabelSize(): LABEL_SIZE | undefined {
    return this.graph.store.getNodeDataNullable<CustomGroupData>(this.nodeId)?.labelFontSize;
  }

  getGroupIsLocked(): boolean {
    return this.graph.getEffectiveIsLocked(this.nodeId);
  }

  toggleLock(): void {
    this.flowView.toggleLockNode(this.nodeId);
  }

  handleResize(requestedBounds: GroupBounds, isOngoing: boolean) {
    const childNodes = this.graph.getChildNodesOfGroup(this.nodeId);
    const groupNode = this.graph.store.getNode(this.nodeId);
    if (!groupNode) {
      logger.warn('handleResize: group node not found ', {
        id: this.nodeId,
      });
      return;
    }
    const newBounds = constrainBoundsToChildren(requestedBounds, childNodes, groupNode.position, DEFAULT_GROUP_PADDING);

    this.graph.edit(() => {
      const undoEntry = UndoRedoEntry.empty();

      undoEntry.add(this.setGroupBoundsAndRebaseChildren(newBounds, groupNode, childNodes));

      if (!isOngoing) {
        // Compute and attach nodes that now intersect with the resized group
        const updatedGroupNode = this.graph.store.getNode(this.nodeId);
        const attachments = this.flowView.findUngroupedNodesIntersectingWithGroups([updatedGroupNode]);
        const nodesToAttach = attachments.find((a) => a.groupId === this.nodeId)?.nodes || [];

        if (nodesToAttach.length > 0) {
          undoEntry.add(this.flowView.attachNodesToGroup(nodesToAttach, this.nodeId));
        }
      }

      return undoEntry;
    }, isOngoing);
  }

  resizeToFit() {
    this.graph.edit(() => {
      const undoEntry = UndoRedoEntry.empty();
      const childNodes = this.graph.getChildNodesOfGroup(this.nodeId);
      const groupNode = this.graph.store.getNode(this.nodeId);

      if (!groupNode) {
        logger.warn('resizeToFit: group node not found', {
          id: this.nodeId,
        });
        return undoEntry;
      }

      if (childNodes.length === 0) {
        logger.warn('resizeToFit: no child nodes found', {
          id: this.nodeId,
        });
        return undoEntry;
      }

      const childNodesAbsolute = childNodes.map((child) => convertChildPositionToAbsolute(child, groupNode.position));
      const newBounds = calculateMultiNodeBoundsWithPadding(childNodesAbsolute, DEFAULT_GROUP_PADDING);

      undoEntry.add(this.setGroupBoundsAndRebaseChildren(newBounds, groupNode, childNodes));

      return undoEntry;
    }, false);
  }

  getChildNodes(): Node[] {
    return this.graph.getChildNodesOfGroup(this.nodeId);
  }

  private getGroupName(): string | undefined {
    return this.graph.store.getNodeDataNullable<CustomGroupData>(this.nodeId)?.name;
  }

  private setGroupBoundsAndRebaseChildren(newBounds: GroupBounds, groupNode: Node, childNodes: Node[]): UndoRedoEntry {
    const { x, y, width, height } = newBounds;

    // Use passed childNodes to avoid refetching after cancelOngoingAction
    const childPositionUpdates = childNodes.map((child) => ({
      nodeId: child.id,
      position: rebaseChildPosition(child, groupNode.position, { x, y }),
      width: child.width!,
      height: child.height!,
    }));
    const undoEntry = UndoRedoEntry.empty();
    undoEntry.add(
      this.flowView.setNodePositions([{ nodeId: this.nodeId, position: { x, y } }, ...childPositionUpdates]),
    );
    undoEntry.add(this.flowView.setNodeDimensions([{ nodeId: this.nodeId, width, height, updateData: true }]));
    return undoEntry;
  }

  private removeNodesFromGroup(nodes: Node[]): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();

    for (const node of nodes) {
      const parentGroup = this.graph.store.getNode(node.parentId!);
      const absolutePosition = convertChildPositionToAbsolute(node, parentGroup.position);

      undoEntry.add(this.graph.store.setNodePosition(node.id, absolutePosition.position));
      undoEntry.add(this.graph.setNodeParent(node.id, undefined));
    }

    return undoEntry;
  }
}
